import React from 'react';
import { User } from '../../../../services/usersApi';
import { RowActionsMenu } from '../../../../components/RowActionsMenu';
import styles from './UsersTable.module.css';

interface UsersTableProps {
  items: User[];
  onEdit?: (id: number) => void;
  onToggleActive?: (id: number) => void;
  onDelete?: (id: number) => void;
}

const ROLE_COLOR: Record<string, { bg: string; color: string }> = {
  'Admin':             { bg: '#FEE2E2', color: '#991B1B' },
  'Sales Rep':         { bg: '#DBEAFE', color: '#1E40AF' },
  'Driver':            { bg: '#FEF3C7', color: '#92400E' },
  'Warehouse Manager': { bg: '#D1FAE5', color: '#065F46' },
  'Call Center':       { bg: '#E0E7FF', color: '#3730A3' },
  'Customer':          { bg: '#F3F4F6', color: '#374151' },
};

export const UsersTable: React.FC<UsersTableProps> = ({ items, onEdit, onToggleActive, onDelete }) => {
  const showActions = !!(onEdit || onToggleActive || onDelete);

  if (items.length === 0) {
    return (
      <div className={styles.empty}>
        No users match the current filter.
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th>Phone</th>
            <th>Employee ID</th>
            <th>Department</th>
            <th>Status</th>
            <th>Joined</th>
            {showActions && <th></th>}
          </tr>
        </thead>
        <tbody>
          {items.map((u) => {
            const rc = ROLE_COLOR[u.role] ?? ROLE_COLOR['Customer'];
            return (
              <tr key={u.id} className={u.is_active ? '' : styles.inactiveRow}>
                <td>
                  <div className={styles.name}>{u.full_name}</div>
                  <div className={styles.email}>{u.email}</div>
                </td>
                <td>
                  <span className={styles.roleBadge} style={{ background: rc.bg, color: rc.color }}>
                    {u.role}
                  </span>
                </td>
                <td className={styles.muted}>{u.phone || '—'}</td>
                <td className={styles.muted}>{u.employee_id || '—'}</td>
                <td className={styles.muted}>{u.department || '—'}</td>
                <td>
                  <span className={`${styles.statusBadge} ${u.is_active ? styles.statusActive : styles.statusInactive}`}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className={styles.muted}>
                  {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </td>
                {showActions && (
                  <td>
                    <RowActionsMenu
                      onEdit={onEdit ? () => onEdit(u.id) : undefined}
                      extra={onToggleActive ? [{
                        label: u.is_active ? 'Deactivate' : 'Activate',
                        onClick: () => onToggleActive(u.id),
                        destructive: u.is_active,
                      }] : []}
                      onDelete={onDelete ? () => onDelete(u.id) : undefined}
                      deleteLabel="Delete"
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default UsersTable;
